import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectItem, SelectTrigger, SelectValue, SelectLabel } from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../src/App.js";
import { SelectContent } from "@radix-ui/react-select";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, Users, TrendingUp, Eye, EyeOff } from "lucide-react";

export const UsersPage = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [passwordErrors, setPasswordErrors] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<string>("");
  const [formData, setFormData] = useState({
    id: "",
    username: "",
    password: "",
    confirmPassword: "",
    mobile: "",
    email: "",
    role: "",
  });
  
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const maxResults = 10;

  // Password validation function
  const validatePassword = (password: string) => {
    const errors: { [key: string]: boolean } = {
      minLength: password.length >= 8,
      hasCapital: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
    return errors;
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Required field validations
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    
    // Strict role validation - must be selected and not empty
    if (!formData.role || formData.role === "") {
      newErrors.role = "Role is required";
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Role-based validations
    if (formData.role === "admin" || formData.role === "staff") {
      // Password validation for roles that need it
      if (!formData.password || formData.password.trim() === "") {
        newErrors.password = "Password is required";
      } else {
        const passwordValidation = validatePassword(formData.password);
        setPasswordErrors(passwordValidation);
        
        // Check if all password requirements are met
        if (!Object.values(passwordValidation).every(v => v)) {
          newErrors.password = "Password does not meet all requirements";
        }
      }

      // Confirm password validation
      if (!formData.confirmPassword || formData.confirmPassword.trim() === "") {
        newErrors.confirmPassword = "Confirm password is required";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.username.trim() &&
      formData.email.trim() &&
      formData.mobile.trim() &&
      formData.role &&
      formData.role !== ""
    );
  };

  // Fetch all users for search and filtering
  const fetchAllUsersForSearch = async (role: string) => {
    try {
      const roleParam = role === "all" ? "allroles" : role;
      const response = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: 10000, role: roleParam },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setAllUsers(response.data.users || []);
      setTotalUsers(response.data.totalCount || 0);
      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
      setAllUsers([]);
      setTotalUsers(0);
      return { users: [], totalCount: 0 };
    }
  };

  // Get paginated records
  const getPaginatedRecords = (recordsToDisplay: any[]) => {
    const startIndex = (page - 1) * maxResults;
    const endIndex = startIndex + maxResults;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  // Fetch users from API
  const fetchUsers = async (page = 1) => {
    try {
      const firstResults = (page - 1) * maxResults;
      // Send "allroles" for the users management page to show all roles
      const roleParam = filterRole === "all" ? "allroles" : filterRole;
      const response = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults, maxResults, role: roleParam },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
      return { users: [], totalCount: 0 };
    }
  };

  // Create or Update user
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (formData.id) {
        await updateUser(formData.id, formData);
      } else {
        const newUser = await createUser(formData);
        setUsers([...users, newUser]);
      }
      const loadUsers = async () => {
        await fetchAllUsersForSearch(filterRole);
      };
      loadUsers();
      setPage(1);
      setIsDrawerOpen(false);
      resetForm();
    } catch (error: any) {
      const errorMsg = error.message || "Error saving user";
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Create user API call
  const createUser = async (userData: any) => {
    try {
      // Filter out unnecessary fields for backend
      const cleanData = {
        username: userData.username,
        password: userData.password,
        mobile: userData.mobile,
        email: userData.email,
        role: userData.role,
      };
      
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/user/create`,
        cleanData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error creating user";
      throw new Error(errorMessage);
    }
  };

  // Update user API call
  const updateUser = async (userId: string, userData: any) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}account/${id}/user/update`,
        { userId, ...userData, originalEmail },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error updating user";
      throw new Error(errorMessage);
    }
  };

  // Delete user
  const handleDelete = async (userId: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(userId);
        setUsers(users.filter((user) => user.id !== userId));
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
  };

  // Delete user API call
  const deleteUser = async (userId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}account/${id}/user/${userId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchAllUsersForSearch(filterRole);
    setPage(1);
  }, [filterRole]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Open the drawer in create mode
  const openDrawerForCreate = () => {
    setFormData({
      id: "",
      username: "",
      password: "",
      confirmPassword: "",
      mobile: "",
      email: "",
      role: "",
    });
    setErrors({});
    setPasswordErrors({});
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  // Open the drawer in edit mode
  const openDrawerForEdit = (user: any) => {
    setFormData(user);
    setOriginalEmail(user.email); // Track original email to avoid duplicate check on same email
    setErrors({});
    setPasswordErrors({});
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: "",
      username: "",
      password: "",
      confirmPassword: "",
      mobile: "",
      email: "",
      role: "",
    });
    setOriginalEmail("");
    setErrors({});
    setPasswordErrors({});
  };

  const filteredUsers = getPaginatedRecords(
    allUsers.filter(
      (user) =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm)
    )
  );

  // Get total filtered count for pagination
  const filteredTotalCount = allUsers.filter(
    (user) =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobile?.includes(searchTerm)
  ).length;

  const totalPages = Math.ceil(filteredTotalCount / maxResults);
  const roleColors: any = {
    admin: "bg-purple-100 text-purple-800",
    staff: "bg-green-100 text-green-800",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
          <Users className="text-purple-600" size={36} />
          Users
        </h1>
        <p className="text-gray-500 text-sm mt-1">Manage and create user accounts efficiently</p>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
          <Input
            placeholder="Search by username, name, email, or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-0 transition-colors"
          />
        </div>

        <select
          className="px-4 py-3 border-2 border-gray-200 rounded-xl bg-white hover:border-purple-300 focus:outline-none focus:border-purple-500 transition-colors font-medium"
          onChange={(e) => setFilterRole(e.target.value)}
          value={filterRole || "all"}
        >
          <option value="all">All Roles</option>
          <option value="admin">👑 Admin</option>
          <option value="staff">👔 Staff</option>
        </select>

        <Button
          onClick={openDrawerForCreate}
          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white flex items-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-purple-600/30"
        >
          <Plus size={20} />
          New User
        </Button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium text-lg">No users found</p>
            <p className="text-gray-400 text-sm mt-1">Create a new user to get started</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Username</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Mobile</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Role</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-purple-50 transition-colors group"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 group-hover:text-purple-700">
                      {user.username || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.email || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.mobile || "-"}</td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 ${
                          roleColors[user.role] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.role === "admin" && "👑"}
                        {user.role === "staff" && "👔"}
                        {user.role || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDrawerForEdit(user)}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50 hover:border-purple-400 font-medium"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 font-medium"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <p>No users found</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{user.username}</p>
                  <p className="text-xs text-gray-500">{user.email || "-"}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    roleColors[user.role] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {user.role}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <p className="text-gray-600">
                  <span className="font-medium">Email:</span> {user.email || "-"}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Mobile:</span> {user.mobile || "-"}
                </p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  size="sm"
                  onClick={() => openDrawerForEdit(user)}
                  className="bg-blue-500 hover:bg-blue-600 text-white gap-1 flex-1 md:flex-none"
                >
                  <Edit2 size={16} />
                  Edit
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(user.id)}
                  className="bg-red-500 hover:bg-red-600 text-white gap-1 flex-1 md:flex-none"
                >
                  <Trash2 size={16} />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {Math.max(1, (page - 1) * maxResults + 1)} to {Math.min(page * maxResults, filteredTotalCount)} of <strong>{filteredTotalCount}</strong> users
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Drawer Form */}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-xl border-2 md:border-2 border-gray-200 md:border-purple-200 shadow-2xl ${
          isDrawerOpen
            ? "translate-y-0 md:scale-100 md:opacity-100"
            : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 sticky top-0 z-10 flex justify-between items-center rounded-t-2xl md:rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold">
              {isCreating ? "➕ New User" : "✏️ Edit User"}
            </h2>
            <p className="text-xs text-purple-100 mt-1">
              {isCreating ? "Add a new team member" : "Update user details"}
            </p>
          </div>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-white hover:bg-purple-500 p-2 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.submit && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3">
              <p className="text-red-700 font-semibold text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <span>👤</span> Personal Information
            </h3>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>👤</span> Username
              </Label>
              <Input
                value={formData.username}
                onChange={(e) => {
                  setFormData({ ...formData, username: e.target.value });
                  setErrors({ ...errors, username: "" });
                }}
                placeholder="Enter username"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.username ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
                required
              />
              {errors.username && <p className="text-red-600 text-xs mt-1">{errors.username}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span></span> Email
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setErrors({ ...errors, email: "" });
                }}
                placeholder="Enter email address"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.email ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
                required
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>📱</span> Mobile Number
              </Label>
              <Input
                type="tel"
                inputMode="numeric"
                value={formData.mobile}
                onChange={(e) => {
                  // Only allow numbers
                  const numbersOnly = e.target.value.replace(/[^0-9]/g, "");
                  setFormData({ ...formData, mobile: numbersOnly });
                  setErrors({ ...errors, mobile: "" });
                }}
                placeholder="10 digit number (without country code)"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.mobile ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
                required
              />
              {errors.mobile && <p className="text-red-600 text-xs mt-1">{errors.mobile}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>👔</span> Role
              </Label>
              <select
                value={formData.role || ""}
                onChange={(e) => {
                  setFormData({ ...formData, role: e.target.value });
                  setErrors({ ...errors, role: "" });
                }}
                className={`w-full mt-2 px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-300 font-medium bg-white ${
                  errors.role ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              >
                <option value="">Select Role</option>
                <option value="admin">👑 Admin</option>
                <option value="staff">👔 Staff</option>
              </select>
              {errors.role && <p className="text-red-600 text-xs mt-1">{errors.role}</p>}
            </div>
          </div>

          {/* Security Section */}
          {(formData.role === "admin" || formData.role === "staff") && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 space-y-4">
              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>🔐</span> Password
                </Label>
                <div className="relative mt-3">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setFormData({ ...formData, password: newPassword });
                      setErrors({ ...errors, password: "" });
                      // Update password validation errors in real-time
                      if (newPassword) {
                        setPasswordErrors(validatePassword(newPassword));
                      }
                    }}
                    placeholder="Enter password"
                    className={`border-2 rounded-lg focus:ring-0 py-2.5 pr-10 ${
                      errors.password ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                    }`}
                    required={isCreating}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-600 text-xs mt-2 font-semibold">{errors.password}</p>}
                
                {/* Password Requirements */}
                {formData.password && (
                  <div className="mt-3 p-3 bg-white border border-orange-200 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                    <div className="space-y-1.5">
                      <div className={`flex items-center gap-2 text-xs ${passwordErrors.minLength ? "text-green-600" : "text-red-600"}`}>
                        <span>{passwordErrors.minLength ? "✓" : "✗"}</span>
                        <span>Minimum 8 characters</span>
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasCapital ? "text-green-600" : "text-red-600"}`}>
                        <span>{passwordErrors.hasCapital ? "✓" : "✗"}</span>
                        <span>At least one capital letter (A-Z)</span>
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasNumber ? "text-green-600" : "text-red-600"}`}>
                        <span>{passwordErrors.hasNumber ? "✓" : "✗"}</span>
                        <span>At least one number (0-9)</span>
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasSpecial ? "text-green-600" : "text-red-600"}`}>
                        <span>{passwordErrors.hasSpecial ? "✓" : "✗"}</span>
                        <span>At least one special character (!@#$%^&* etc.)</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-orange-600 mt-2">
                  {isCreating ? "Required for new users" : "Leave blank to keep current password"}
                </p>
              </div>

              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>🔐</span> Confirm Password
                </Label>
                <div className="relative mt-3">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, confirmPassword: e.target.value });
                      setErrors({ ...errors, confirmPassword: "" });
                    }}
                    placeholder="Confirm password"
                    className={`border-2 rounded-lg focus:ring-0 py-2.5 pr-10 ${
                      errors.confirmPassword ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                    }`}
                    required={isCreating}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-600 text-xs mt-2">{errors.confirmPassword}</p>}
                {formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-green-600 text-xs mt-2">✓ Passwords match</p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
            <Button
              type="submit"
              disabled={loading || !isFormValid()}
              className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-2.5 rounded-lg shadow-lg shadow-purple-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isCreating ? "Creating..." : "Updating...") : (isCreating ? "✓ Create User" : "✓ Update User")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDrawerOpen(false)}
              className="flex-1 border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 py-2.5 rounded-lg"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
